import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

const EyeIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24M1 1l22 22"/>
  </svg>
);
const PasswordField = ({ value, onChange, placeholder, disabled, required }) => {
  const [show, setShow] = React.useState(false);
  return (
    <div className="pwd-wrap">
      <input className="field-input" type={show ? 'text' : 'password'}
        placeholder={placeholder} value={value} onChange={onChange}
        disabled={disabled} required={required} />
      <button type="button" className="pwd-toggle" onClick={() => setShow(s => !s)} tabIndex={-1}>
        {show ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
};

const Login = () => {
  const [tab, setTab] = useState('login'); // 'login' | 'register'
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetErr, setResetErr] = useState('');

  const { login, register, loading, error, isAuthenticated, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/gestion-budget/dashboard';

  useEffect(() => {
    if (!loading && isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, loading, navigate, from]);

  useEffect(() => { clearError(); }, [clearError]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLocalError('');
    setIsSubmitting(true);
    const result = await login(loginData);
    setIsSubmitting(false);
    if (result.success) navigate(from, { replace: true });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLocalError('');
    if (!registerData.name.trim()) { setLocalError('Le nom est requis.'); return; }
    setIsSubmitting(true);
    const result = await register(registerData);
    setIsSubmitting(false);
    if (result.success) navigate('/gestion-budget/dashboard', { replace: true });
  };

  const handleReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) { setResetErr('Email requis.'); return; }
    setResetLoading(true);
    setResetErr('');
    setResetMsg('');
    try {
      await authAPI.requestPasswordReset(resetEmail);
      setResetMsg('Si un compte existe, un lien de réinitialisation a été envoyé.');
    } catch (err) {
      setResetErr(err?.response?.data?.message || 'Impossible d\'envoyer l\'email.');
    } finally {
      setResetLoading(false);
    }
  };

  const displayError = error || localError;

  return (
    <div className="auth-root">

      <div className="auth-card">
        <div className="auth-card-inner">
          <div className="auth-logo">
            <img src="/dub-hub-logo.svg" alt="Dub Gestion Hub" className="auth-logo-dub" />
          </div>

          {!resetMode ? (
            <>
              <div className="auth-tabs">
                <div className={`auth-tab-indicator${tab === 'register' ? ' register' : ''}`} />
                <button className={`auth-tab${tab === 'login' ? ' active' : ''}`} onClick={() => { setTab('login'); clearError(); setLocalError(''); }}>
                  Connexion
                </button>
                <button className={`auth-tab${tab === 'register' ? ' active' : ''}`} onClick={() => { setTab('register'); clearError(); setLocalError(''); }}>
                  Inscription
                </button>
              </div>

              {displayError && <div className="auth-error" style={{ marginBottom: 16 }}>{displayError}</div>}

              {tab === 'login' ? (
                <form className="auth-form" onSubmit={handleLogin}>
                  <div className="field-group">
                    <label className="field-label">Adresse email</label>
                    <input className="field-input" type="email" placeholder="vous@exemple.fr" value={loginData.email}
                      onChange={e => setLoginData(d => ({ ...d, email: e.target.value }))} required disabled={isSubmitting} />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Mot de passe</label>
                    <PasswordField value={loginData.password} placeholder="••••••••" required disabled={isSubmitting}
                      onChange={e => setLoginData(d => ({ ...d, password: e.target.value }))} />
                  </div>
                  <div className="auth-forgot">
                    <button type="button" className="link-btn" onClick={() => { setResetMode(true); setResetEmail(loginData.email); }}>
                      Mot de passe oublié ?
                    </button>
                  </div>
                  <button type="submit" className={`btn-primary full${isSubmitting ? ' loading' : ''}`} disabled={isSubmitting}>
                    {isSubmitting ? <span className="spinner" /> : null}
                    {isSubmitting ? 'Connexion...' : 'Se connecter'}
                  </button>
                </form>
              ) : (
                <form className="auth-form" onSubmit={handleRegister}>
                  <div className="field-group">
                    <label className="field-label">Nom complet</label>
                    <input className="field-input" type="text" placeholder="Prénom Nom" value={registerData.name}
                      onChange={e => setRegisterData(d => ({ ...d, name: e.target.value }))} required disabled={isSubmitting} />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Adresse email</label>
                    <input className="field-input" type="email" placeholder="vous@exemple.fr" value={registerData.email}
                      onChange={e => setRegisterData(d => ({ ...d, email: e.target.value }))} required disabled={isSubmitting} />
                  </div>
                  <div className="field-group">
                    <label className="field-label">Mot de passe</label>
                    <PasswordField value={registerData.password} placeholder="••••••••" required disabled={isSubmitting}
                      onChange={e => setRegisterData(d => ({ ...d, password: e.target.value }))} />
                  </div>
                  <button type="submit" className={`btn-primary full${isSubmitting ? ' loading' : ''}`} disabled={isSubmitting}>
                    {isSubmitting ? <span className="spinner" /> : null}
                    {isSubmitting ? 'Inscription...' : 'Créer un compte'}
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Réinitialiser le mot de passe</h3>
              {resetErr && <div className="auth-error" style={{ marginBottom: 14 }}>{resetErr}</div>}
              {resetMsg && <div className="auth-success" style={{ marginBottom: 14 }}>{resetMsg}</div>}
              <form className="auth-form" onSubmit={handleReset}>
                <div className="field-group">
                  <label className="field-label">Adresse email</label>
                  <input className="field-input" type="email" placeholder="vous@exemple.fr" value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)} required disabled={resetLoading} />
                </div>
                <button type="submit" className={`btn-primary full${resetLoading ? ' loading' : ''}`} disabled={resetLoading}>
                  {resetLoading ? 'Envoi...' : 'Envoyer le lien'}
                </button>
              </form>
              <div className="auth-switch" style={{ marginTop: 16 }}>
                <button type="button" className="link-btn" onClick={() => { setResetMode(false); setResetErr(''); setResetMsg(''); }}>
                  ← Retour à la connexion
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
